import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Sparkles, Loader2, Send, Clock, Star } from "lucide-react";
import { api } from "@/lib/api";
import { CONTACT_INFO, PRICING, ASSETS } from "@/data/site";

const ContactSection = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", session_type: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [news, setNews] = useState({ email: "", name: "" });
  const [newsLoading, setNewsLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const updateNews = (k) => (e) => setNews((n) => ({ ...n, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        session_type: form.session_type || null,
        message: form.message.trim(),
      });
      toast.success("Message sent ✨ Verna will get back to you soon.");
      setForm({ name: "", email: "", phone: "", session_type: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again or call Verna directly.");
    } finally {
      setLoading(false);
    }
  };

  const handleNewsSubmit = async (e) => {
    e.preventDefault();
    if (!news.email.trim()) {
      toast.error("Please enter your email.");
      return;
    }
    setNewsLoading(true);
    try {
      await api.post("/newsletter", { email: news.email.trim(), name: news.name.trim() || null });
      toast.success("You're on the list ✨ Wisdom & workshop updates incoming.");
      setNews({ email: "", name: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409) {
        toast.info("You're already subscribed — thank you!");
      } else {
        toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
      }
    } finally {
      setNewsLoading(false);
    }
  };

  const inputClass = "w-full px-5 py-3.5 rounded-full bg-white/70 border border-[#131a48]/20 text-[#131a48] placeholder:text-[#131a48]/45 focus:outline-none focus:border-[#131a48]/60 transition";
  const labelClass = "block text-xs uppercase tracking-widest mb-2";
  const labelStyle = { color: "rgba(19,26,72,0.75)" };

  return (
    <section id="contact" className="relative section section-light" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-xs tracking-[0.3em] uppercase mb-3" style={{ color: "var(--cs-purple)" }}>Get in touch</p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="font-serif-display text-5xl md:text-6xl leading-tight"
            style={{ color: "var(--cs-navy)" }}
            data-testid="contact-heading"
          >
            Book a <span className="italic" style={{ color: "var(--cs-purple)" }}>personal reading</span>
          </motion.h2>
          <p className="mt-5 text-lg leading-relaxed" style={{ color: "#475569" }}>
            Send a quick message and Verna will get back to you to arrange a time. Or pick up the phone — she'd love to hear from you.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* LEFT: Send me a message — DARK BOX on white */}
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="box-pastel-lavender p-8 md:p-10 space-y-5"
            style={{ boxShadow: "0 12px 40px rgba(74,42,114,0.22)" }}
            data-testid="contact-form"
          >
            <h3 className="font-serif-display text-3xl text-[#131a48] mb-1">Send me a message</h3>
            <p className="text-sm mb-3" style={{ color: "rgba(19,26,72,0.75)" }}>Verna will reply by email or phone, usually within 24–48 hours.</p>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass} style={labelStyle}>Your name</label>
                <input type="text" required value={form.name} onChange={update("name")} className={inputClass} placeholder="Jane Doe" data-testid="contact-name-input" />
              </div>
              <div>
                <label className={labelClass} style={labelStyle}>Email</label>
                <input type="email" required value={form.email} onChange={update("email")} className={inputClass} placeholder="you@example.com" data-testid="contact-email-input" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass} style={labelStyle}>Phone (optional)</label>
                <input type="tel" value={form.phone} onChange={update("phone")} className={inputClass} placeholder="027 000 0000" data-testid="contact-phone-input" />
              </div>
              <div>
                <label className={labelClass} style={labelStyle}>Session of interest</label>
                <div className="relative">
                  <Clock className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: "rgba(19,26,72,0.5)" }} />
                  <select
                    value={form.session_type} onChange={update("session_type")}
                    className="w-full pl-12 pr-5 py-3.5 rounded-full bg-white/70 border border-[#131a48]/20 text-[#131a48] focus:outline-none focus:border-[#131a48]/60 transition appearance-none cursor-pointer"
                    data-testid="contact-session-select"
                  >
                    <option value="" className="bg-white text-[#131a48]">Choose a session…</option>
                    {PRICING.map((p) => (
                      <option key={p.id} value={p.duration} className="bg-white text-[#131a48]">{p.duration} — {p.price}</option>
                    ))}
                    <option value="Not sure yet" className="bg-white text-[#131a48]">Not sure yet</option>
                  </select>
                </div>
              </div>
            </div>

            <div>
              <label className={labelClass} style={labelStyle}>Your message</label>
              <textarea required rows={5} value={form.message} onChange={update("message")}
                className="w-full px-5 py-4 rounded-3xl bg-white/70 border border-[#131a48]/20 text-[#131a48] placeholder:text-[#131a48]/45 focus:outline-none focus:border-[#131a48]/60 transition resize-none"
                placeholder="Tell Verna a little about what's on your mind, or ask any question you have…"
                data-testid="contact-message-input"
              />
            </div>

            <button type="submit" disabled={loading} className="btn-pill-primary w-full disabled:opacity-60" data-testid="contact-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {loading ? "Sending…" : "Send message"}
            </button>
          </motion.form>

          {/* RIGHT: Photo (square) + Get in Touch + Pricing + Newsletter */}
          <motion.aside
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="space-y-6"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              {/* Square Verna photo */}
              <div className="relative rounded-3xl overflow-hidden aspect-square" style={{ background: "#F1F4FA", boxShadow: "0 12px 40px rgba(19,26,72,0.18)" }}>
                <img
                  src={ASSETS.vernaDesk}
                  alt="Verna reading the cards"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: "center 25%" }}
                  data-testid="contact-verna-image"
                />
                <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-[#131a48]/95 via-[#131a48]/40 to-transparent">
                  <p className="font-serif-display text-lg text-white italic">"Come sit with the cards."</p>
                  <p className="text-xs text-white/80 mt-0.5">— Verna</p>
                </div>
              </div>

              {/* Get in Touch */}
              <div className="box-pastel-lavender p-6" data-testid="get-in-touch" style={{ boxShadow: "0 12px 40px rgba(74,42,114,0.22)" }}>
                <h3 className="font-serif-display text-2xl text-[#131a48] mb-4">Get in Touch</h3>
                <ul className="space-y-4">
                  <li>
                    <a href={CONTACT_INFO.phoneHref} className="flex items-start gap-3 group" data-testid="contact-phone-link">
                      <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "linear-gradient(135deg, var(--cs-navy), var(--cs-blue-dk))" }}>
                        <Phone className="w-4 h-4 text-white" />
                      </span>
                      <div>
                        <p className="text-[10px] uppercase tracking-widest" style={{ color: "rgba(19,26,72,0.6)" }}>Call</p>
                        <p className="text-[#131a48] text-sm md:text-base">{CONTACT_INFO.phone}</p>
                      </div>
                    </a>
                  </li>
                  <li>
                    <a href={CONTACT_INFO.emailHref} className="flex items-start gap-3 group" data-testid="contact-email-link">
                      <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "linear-gradient(135deg, var(--cs-navy), var(--cs-blue-dk))" }}>
                        <Mail className="w-4 h-4 text-white" />
                      </span>
                      <div>
                        <p className="text-[10px] uppercase tracking-widest" style={{ color: "rgba(19,26,72,0.6)" }}>Email</p>
                        <p className="text-[#131a48] text-sm break-all">{CONTACT_INFO.email}</p>
                      </div>
                    </a>
                  </li>
                  <li>
                    <div className="flex items-start gap-3">
                      <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "linear-gradient(135deg, var(--cs-navy), var(--cs-blue-dk))" }}>
                        <MapPin className="w-4 h-4 text-white" />
                      </span>
                      <div>
                        <p className="text-[10px] uppercase tracking-widest" style={{ color: "rgba(19,26,72,0.6)" }}>Find me</p>
                        <p className="text-[#131a48] text-sm">{CONTACT_INFO.address}</p>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            {/* Pricing */}
            <div className="box-pastel-lavender p-7" data-testid="sidebar-pricing" style={{ boxShadow: "0 12px 40px rgba(74,42,114,0.22)" }}>
              <p className="text-xs uppercase tracking-[0.25em] mb-4" style={{ color: "var(--cs-blue-dk)" }}>Sessions & pricing</p>
              <ul className="space-y-4">
                {PRICING.map((p) => (
                  <li key={p.id} className="flex items-start justify-between gap-4 pb-4 border-b border-[#131a48]/15 last:border-b-0 last:pb-0" data-testid={`sidebar-pricing-${p.id}`}>
                    <div>
                      <p className="text-[#131a48] font-medium">{p.duration}</p>
                      <p className="text-xs mt-1" style={{ color: "rgba(19,26,72,0.7)" }}>{p.desc}</p>
                    </div>
                    <p className="font-serif-display text-2xl shrink-0" style={{ color: "var(--cs-blue-dk)" }}>{p.price}</p>
                  </li>
                ))}
              </ul>
            </div>

            {/* Newsletter */}
            <form onSubmit={handleNewsSubmit} className="box-pastel-lavender p-7 relative overflow-hidden" data-testid="newsletter-form" style={{ boxShadow: "0 12px 40px rgba(74,42,114,0.22)" }}>
              <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full blur-3xl opacity-30"
                   style={{ background: "radial-gradient(circle, var(--cs-blue-dk), transparent 70%)" }} />
              <div className="relative">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] mb-3" style={{ color: "var(--cs-blue-dk)" }}>
                  <Star className="w-3.5 h-3.5 fill-current" /> Free Newsletter
                </div>
                <h3 className="font-serif-display text-2xl text-[#131a48] mb-2">Spiritual wisdom in your inbox</h3>
                <p className="text-sm mb-4" style={{ color: "rgba(19,26,72,0.75)" }}>
                  Gentle reflections on consciousness, intuition, and upcoming workshops.
                </p>
                <div className="space-y-3">
                  <input
                    type="text" placeholder="Your name (optional)"
                    value={news.name} onChange={updateNews("name")}
                    className={inputClass}
                    data-testid="newsletter-name-input"
                  />
                  <div className="relative">
                    <Mail className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "rgba(19,26,72,0.5)" }} />
                    <input
                      type="email" required placeholder="you@example.com"
                      value={news.email} onChange={updateNews("email")}
                      className="w-full pl-12 pr-5 py-3 rounded-full bg-white/70 border border-[#131a48]/20 text-[#131a48] placeholder:text-[#131a48]/45 focus:outline-none focus:border-[#131a48]/60 transition"
                      data-testid="newsletter-email-input"
                    />
                  </div>
                  <button type="submit" disabled={newsLoading} className="btn-pill-primary w-full disabled:opacity-60" data-testid="newsletter-submit-btn">
                    {newsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {newsLoading ? "Subscribing..." : "Subscribe"}
                  </button>
                </div>
              </div>
            </form>
          </motion.aside>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;

